import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Search, Copy, Check, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

// Catalogo placeholder supportati dai template DOCX
// Sync con server.py → generate_from_docx_template / generate_from_docx_template_ul
const SECTIONS = [
  {
    title: 'Anagrafica Dipendente / Corsista',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    placeholders: [
      { name: 'Nome_corsista', desc: 'Nome del dipendente', sample: 'Mario' },
      { name: 'Cognome_corsista', desc: 'Cognome del dipendente', sample: 'Rossi' },
      { name: 'Nome_Designato', desc: 'Alias per Designazione AA/PS — nome dipendente', sample: 'Mario' },
      { name: 'Cognome_Designato', desc: 'Alias per Designazione AA/PS — cognome', sample: 'Rossi' },
      { name: 'Nome_Cognome', desc: 'Nome e cognome concatenati', sample: 'Mario Rossi' },
      { name: 'Cognome_Nome', desc: 'Cognome e nome concatenati', sample: 'Rossi Mario' },
      { name: 'nome', desc: 'Alias lowercase di Nome_corsista', sample: 'Mario' },
      { name: 'cognome', desc: 'Alias lowercase di Cognome_corsista', sample: 'Rossi' },
      { name: 'Cod_Fiscale', desc: 'Codice fiscale dipendente', sample: 'RSSMRA80A01H501Z' },
      { name: 'Codice_Fiscale', desc: 'Alias di Cod_Fiscale', sample: 'RSSMRA80A01H501Z' },
      { name: 'Corsista_il', desc: 'Data di nascita (gg/mm/aaaa)', sample: '01/01/1980' },
      { name: 'Data_Nascita', desc: 'Alias di Corsista_il', sample: '01/01/1980' },
      { name: 'Corsista_Nato', desc: 'Luogo di nascita', sample: 'Roma' },
      { name: 'Luogo_Nascita', desc: 'Alias di Corsista_Nato', sample: 'Roma' },
      { name: 'Corsista_Nato_Prov', desc: 'Provincia di nascita', sample: 'RM' },
      { name: 'Mansione_unilav', desc: 'Mansione (unilav)', sample: 'Operaio specializzato' },
      { name: 'Mansione', desc: 'Alias di Mansione_unilav', sample: 'Operaio specializzato' },
      { name: 'Data_Assunzione', desc: 'Data di assunzione', sample: '01/03/2020' },
      { name: 'Tipo_Contratto', desc: 'Tipologia contratto', sample: 'Tempo Indeterminato' },
      { name: 'Indirizzo_Residenza', desc: 'Indirizzo di residenza', sample: 'Via Garibaldi 12' },
      { name: 'Citta_Residenza', desc: 'Città di residenza', sample: 'Roma' },
      { name: 'Provincia_Residenza', desc: 'Provincia residenza', sample: 'RM' },
      { name: 'CAP_Residenza', desc: 'CAP residenza', sample: '00100' },
      { name: 'Email_Dipendente', desc: 'Email del dipendente', sample: 'mario.rossi@email.it' },
      { name: 'Telefono_Dipendente', desc: 'Telefono dipendente', sample: '+39 333 1234567' },
    ],
  },
  {
    title: 'Azienda / Unità Locale',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    placeholders: [
      { name: 'Azienda_organizzatrice', desc: 'Ragione sociale azienda', sample: 'CEN Engineering SRL' },
      { name: 'Azianda_organizzatrice', desc: '(Refuso tollerato) — alias precedente', sample: 'CEN Engineering SRL' },
      { name: 'Ragione_Sociale', desc: 'Alias ragione sociale', sample: 'CEN Engineering SRL' },
      { name: 'Nome_Azienda', desc: 'Alias ragione sociale', sample: 'CEN Engineering SRL' },
      { name: 'Azienda', desc: 'Alias breve azienda', sample: 'CEN Engineering SRL' },
      { name: 'azienda', desc: 'Alias lowercase azienda', sample: 'CEN Engineering SRL' },
      { name: 'Sede_Azienda', desc: 'Indirizzo sede UL', sample: 'Via Roma 1' },
      { name: 'Indirizzo_UL', desc: 'Alias di Sede_Azienda', sample: 'Via Roma 1' },
      { name: 'Indirizzo_Azienda', desc: 'Alias di Sede_Azienda', sample: 'Via Roma 1' },
      { name: 'Locale_Sito_In', desc: 'Sede completa (via, CAP, città, prov)', sample: 'Via Roma 1, 91025 Marsala (TP)' },
      { name: 'Luogo_Azienda', desc: 'Città azienda', sample: 'Marsala' },
      { name: 'Luogo_operativa', desc: 'Alias Designazione — città UL', sample: 'Marsala' },
      { name: 'Luogo_Sede', desc: 'Alias città sede', sample: 'Marsala' },
      { name: 'Luogo', desc: 'Alias breve città', sample: 'Marsala' },
      { name: 'Citta_UL', desc: 'Città UL', sample: 'Marsala' },
      { name: 'Citta_Azienda', desc: 'Città azienda', sample: 'Marsala' },
      { name: 'Provincia', desc: 'Provincia UL', sample: 'TP' },
      { name: 'Cap', desc: 'CAP UL', sample: '91025' },
      { name: 'CAP', desc: 'Alias maiuscolo CAP', sample: '91025' },
      { name: 'PIVA', desc: 'Partita IVA', sample: '12345678901' },
      { name: 'P_IVA', desc: 'Alias P.IVA', sample: '12345678901' },
      { name: 'Cod_Fiscale_Azienda', desc: 'Cod. Fiscale azienda', sample: '12345678901' },
      { name: 'SDI', desc: 'Codice SDI', sample: 'ABC1234' },
      { name: 'cod_Ateco', desc: 'Codice ATECO', sample: '71.12.1' },
      { name: 'Email_Aziendale', desc: 'Email azienda', sample: 'info@cen.it' },
      { name: 'Mail_Aziendale', desc: 'Alias email azienda', sample: 'info@cen.it' },
      { name: 'PEC_Aziendale', desc: 'PEC azienda', sample: 'cen@pec.it' },
      { name: 'Telefono_Aziendale', desc: 'Telefono azienda', sample: '0923 123456' },
    ],
  },
  {
    title: 'Datore di Lavoro / Legale Rappresentante',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    placeholders: [
      { name: 'Nome_Datore_Lavoro', desc: 'Nome del datore di lavoro (manager UL)', sample: 'Sergio Celona' },
      { name: 'Datore', desc: 'Alias breve datore', sample: 'Sergio Celona' },
      { name: 'Datore_Lavoro', desc: 'Alias datore', sample: 'Sergio Celona' },
      { name: 'Legale_Rappresentante', desc: 'Alias legale rappresentante', sample: 'Sergio Celona' },
      { name: 'Rappresentante_Legale', desc: 'Alias rappresentante legale', sample: 'Sergio Celona' },
      { name: 'Nome_Datore', desc: 'Alias breve nome datore', sample: 'Sergio Celona' },
      { name: 'nome_datore', desc: 'Alias lowercase nome datore', sample: 'Sergio Celona' },
    ],
  },
  {
    title: 'RSPP / Professionista (firma)',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    placeholders: [
      { name: 'Titolo_RSPP', desc: 'Titolo professionale RSPP (Ing., Dott., ...)', sample: 'Ing.' },
      { name: 'Qualifica_RSPP', desc: 'Qualifica RSPP', sample: 'Ing.' },
      { name: 'Titolo_Nome_RSPP', desc: 'Titolo + nome RSPP', sample: 'Ing. Sergio Celona' },
      { name: 'Nome_RSPP_Completo', desc: 'Alias di Titolo_Nome_RSPP', sample: 'Ing. Sergio Celona' },
      { name: 'Titolo_Professionale', desc: 'Titolo professionale del firmatario', sample: 'Ing.' },
      { name: 'Titolo_Corsista', desc: 'Titolo personale del corsista', sample: 'Sig.' },
      { name: 'Titolo_Dipendente', desc: 'Alias di Titolo_Corsista', sample: 'Sig.' },
    ],
  },
  {
    title: 'Date documento / corso',
    color: 'bg-rose-100 text-rose-800 border-rose-200',
    placeholders: [
      { name: 'Data_Odierna', desc: 'Data del documento (dal campo "Data documento")', sample: '28/05/2026' },
      { name: 'Data', desc: 'Alias di Data_Odierna', sample: '28/05/2026' },
      { name: 'data', desc: 'Alias lowercase data', sample: '28/05/2026' },
      { name: 'Data_incarico', desc: 'Data incarico/designazione', sample: '28/05/2026' },
      { name: 'Data_Designazione', desc: 'Data designazione (AA/PS)', sample: '28/05/2026' },
      { name: 'Data_consegna_DPI', desc: 'Data consegna DPI', sample: '28/05/2026' },
      { name: 'Data_corso', desc: 'Data del corso', sample: '28/05/2026' },
      { name: 'Periodo_dal', desc: 'Inizio corso (= Data_corso)', sample: '28/05/2026' },
      { name: 'Periodo_al', desc: 'Fine corso (= Data_corso)', sample: '28/05/2026' },
      { name: 'Anno_corso', desc: 'Anno del corso', sample: '2026' },
      { name: 'Numero_corso', desc: 'Numero corso univoco', sample: '2026-ABCD' },
      { name: 'Numero_Attestato', desc: 'Numero attestato', sample: 'ABCD1234' },
      { name: 'Luogo_Designazione', desc: 'Luogo designazione (= città UL)', sample: 'Marsala' },
      { name: 'Luogo_Data_Firma', desc: 'Luogo + ", lì " + data firma', sample: 'Marsala, lì 28/05/2026' },
      { name: 'Data_Firma', desc: 'Data firma (= Data_Odierna)', sample: '28/05/2026' },
    ],
  },
  {
    title: 'DPI (Dispositivi di Protezione)',
    color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    placeholders: [
      { name: 'DPI_Lista', desc: 'DPI in formato concatenato', sample: 'Casco; Guanti; Scarpe' },
      { name: 'DPI_Lista_Bullets', desc: 'DPI uno per riga con bullet', sample: '• Casco\n• Guanti' },
      { name: 'DPI_ROW', desc: 'Marker nella prima cella di una riga modello tabella DPI — viene clonata per ogni DPI', sample: '(riga tabella)' },
      { name: 'DPI_Nome', desc: 'Nome DPI nella riga modello', sample: 'Casco' },
      { name: 'DPI_Numero', desc: 'Numero progressivo DPI', sample: '1' },
      { name: 'DPI_Note', desc: 'Note DPI', sample: '' },
    ],
  },
  {
    title: 'Firma autografa (immagine PNG/JPG embedded)',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    placeholders: [
      { name: 'Firma', desc: 'Firma del creatore (immagine, auto-resize 1.6")', sample: '[immagine]' },
      { name: 'Firma_RSPP', desc: 'Alias firma RSPP', sample: '[immagine]' },
      { name: 'Firma_Datore', desc: 'Alias firma datore', sample: '[immagine]' },
      { name: 'Firma_Datore_Lavoro', desc: 'Alias firma datore di lavoro', sample: '[immagine]' },
      { name: 'Firma_Creatore', desc: 'Alias firma creatore', sample: '[immagine]' },
      { name: 'Firma_Professionista', desc: 'Alias firma professionista', sample: '[immagine]' },
    ],
  },
  {
    title: 'Registri Formazione (v36-v37) — dati corso',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    placeholders: [
      { name: 'Codice_Progetto', desc: 'Codice progetto/finanziamento', sample: 'PRJ-2026-001' },
      { name: 'Titolo_Corso', desc: 'Titolo del corso', sample: 'Sicurezza Generale' },
      { name: 'Tipologia_Corso', desc: 'Tipologia (RLS, Antincendio, ecc.)', sample: 'Antincendio Liv. 2' },
      { name: 'Ore_Totali', desc: 'Ore totali del corso', sample: '4' },
      { name: 'Data_Inizio', desc: 'Data inizio corso', sample: '15/03/2026' },
      { name: 'Data_Fine', desc: 'Data fine corso', sample: '15/03/2026' },
      { name: 'Ora_Inizio', desc: 'Ora inizio lezione', sample: '09:00' },
      { name: 'Ora_Fine', desc: 'Ora fine lezione', sample: '13:00' },
      { name: 'Orario', desc: 'Orario formattato (auto)', sample: '09:00 - 13:00' },
      { name: 'Aula', desc: 'Aula/sede svolgimento', sample: 'Aula A' },
      { name: 'Luogo_Corso', desc: 'Località', sample: 'Milano' },
      { name: 'Docente_Nome', desc: 'Docente principale', sample: 'Ing. Mario Rossi' },
      { name: 'Codocente_Nome', desc: 'Codocente (opzionale)', sample: '' },
      { name: 'Firma_Docente', desc: 'Firma autografa docente (immagine)', sample: '[immagine]' },
      { name: 'Numero_Partecipanti', desc: 'Numero totale dei discenti iscritti al registro (v38)', sample: '12' },
      { name: 'N_Partecipanti', desc: 'Alias di Numero_Partecipanti', sample: '12' },
      { name: 'Totale_Discenti', desc: 'Alias di Numero_Partecipanti', sample: '12' },
      { name: 'Numero_Pagine', desc: 'Numero totale di pagine (campo dinamico Word NUMPAGES, v38)', sample: '3' },
      { name: 'Numero_Pagina', desc: 'Numero della pagina corrente (campo dinamico Word PAGE, usalo nel footer, v38)', sample: '1' },
      { name: 'Pagina_di_Totale', desc: 'Stringa composta "X di Y" (PAGE di NUMPAGES, v38)', sample: '1 di 3' },
      { name: 'Data_1', desc: 'Data 1ª giornata DD/MM/YYYY (v38, supporta fino a 10 giornate)', sample: '15/06/2026' },
      { name: 'Ora_Inizio_1', desc: 'Ora inizio 1ª giornata (v38)', sample: '09.00' },
      { name: 'Ora_Fine_1', desc: 'Ora fine 1ª giornata (v38)', sample: '13.00' },
      { name: 'Orario_1', desc: 'Orario 1ª giornata HH:MM - HH:MM (v38)', sample: '09.00 - 13.00' },
      { name: 'Giornata_1', desc: '1ª giornata compatta DD/MM HH:MM-HH:MM (v38)', sample: '15/06/2026 09.00 - 13.00' },
      { name: 'Data_2', desc: 'Data 2ª giornata, idem per Data_3, Data_4 ... fino a Data_10 (v38)', sample: '16/06/2026' },
      { name: 'Ora_Inizio_2', desc: 'Ora inizio 2ª giornata (v38)', sample: '14.00' },
      { name: 'Ora_Fine_2', desc: 'Ora fine 2ª giornata (v38)', sample: '18.00' },
      { name: 'Date_Orari', desc: 'Riepilogo testuale inline di tutte le giornate (v38)', sample: '15/06/2026 (09:00-13:00), 16/06/2026 (14:00-18:00)' },
      { name: 'Numero_Giornate', desc: 'Numero totale di giornate del corso (v38)', sample: '2' },
    ],
  },
  {
    title: 'Registri Formazione — tabella discenti (riga ripetuta)',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    placeholders: [
      { name: 'Discente_Cognome', desc: 'Cognome (in riga tabella replicata)', sample: 'Rossi' },
      { name: 'Discente_Nome', desc: 'Nome', sample: 'Mario' },
      { name: 'Discente_CF', desc: 'Codice fiscale', sample: 'RSSMRA80A01H501Z' },
      { name: 'Discente_Mansione', desc: 'Mansione/job title', sample: 'Operaio' },
      { name: 'Discente_Azienda', desc: 'Azienda di appartenenza', sample: 'Acme SRL' },
      { name: 'Discente_Firma', desc: 'Firma del discente (immagine se firmato digitalmente; cella vuota altrimenti)', sample: '[immagine]' },
    ],
  },
  {
    title: 'HACCP Templates (v37) — anagrafica',
    color: 'bg-rose-100 text-rose-800 border-rose-200',
    placeholders: [
      { name: 'Responsabile_HACCP', desc: 'Nome responsabile HACCP', sample: 'Mario Rossi' },
      { name: 'Firma_Responsabile_HACCP', desc: 'Firma responsabile HACCP (immagine)', sample: '[immagine]' },
      { name: 'Consulente_HACCP', desc: 'Consulente HACCP esterno', sample: 'Studio Tecnico SRL' },
      { name: 'Anno_Riferimento', desc: 'Anno di riferimento', sample: '2026' },
      { name: 'Mese_Riferimento', desc: 'Mese di riferimento', sample: 'Marzo 2026' },
      { name: 'Numero_Banco_Frigo', desc: 'ID/nome banco frigo', sample: 'Frigo cucina' },
      { name: 'Tipologia_Banco', desc: 'Tipologia banco frigo', sample: 'Refrigerato' },
      { name: 'Range_Temperatura', desc: 'Range previsto', sample: '0/+4 °C' },
      { name: 'Codice_Lotto', desc: 'Codice lotto prelievo', sample: 'L-2026-001' },
      { name: 'Data_Prelievo', desc: 'Data prelievo campione', sample: '15/03/2026' },
      { name: 'Operatore', desc: 'Nome operatore', sample: 'Mario Rossi' },
      { name: 'Allergene', desc: 'Nome allergene', sample: 'Glutine' },
    ],
  },
];

export default function TemplateVariablesPage() {
  const [search, setSearch] = useState('');
  const [copiedKey, setCopiedKey] = useState('');

  const totalCount = useMemo(
    () => SECTIONS.reduce((s, sec) => s + sec.placeholders.length, 0),
    [],
  );

  const filteredSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS.map((sec) => ({
      ...sec,
      placeholders: sec.placeholders.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.desc.toLowerCase().includes(q) ||
          p.sample.toLowerCase().includes(q),
      ),
    })).filter((sec) => sec.placeholders.length > 0);
  }, [search]);

  const handleCopy = (name) => {
    const text = `«${name}»`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(name);
      toast.success(`Copiato: ${text}`);
      setTimeout(() => setCopiedKey(''), 1500);
    });
  };

  return (
    <div className="space-y-6" data-testid="template-variables-page">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight">Variabili Template DOCX</h1>
          <Badge variant="secondary" className="ml-2">{totalCount} placeholder</Badge>
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Elenco completo dei <strong>placeholder</strong> supportati nei template Word (<code>.docx</code>) usati
          da CEN per la generazione documenti. Inserisci queste variabili nel tuo file Word con la sintassi{' '}
          <code className="px-1.5 py-0.5 bg-muted rounded text-xs">«Nome_Variabile»</code> (le virgolette doppie
          basse). Al momento della generazione verranno sostituite con i dati reali del dipendente / UL.
        </p>
        <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 px-4 py-3 text-xs text-amber-900 max-w-3xl">
          <strong>Suggerimento:</strong> in Word digita <em>Inserisci → Simbolo</em> e cerca i caratteri{' '}
          <code>«</code> (U+00AB) e <code>»</code> (U+00BB), oppure copia il template di esempio dalla scheda
          "Documenti → Carica Template". Clicca un placeholder per copiarlo negli appunti.
        </div>
      </header>

      <div className="sticky top-0 z-10 bg-background pb-2">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca placeholder (es. 'datore', 'data', 'designato'...)"
            className="pl-9"
            data-testid="template-vars-search"
          />
        </div>
      </div>

      {filteredSections.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            Nessun placeholder trovato per <strong>"{search}"</strong>.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredSections.map((section) => (
          <Card key={section.title} className="overflow-hidden" data-testid={`template-section-${section.title}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>{section.title}</span>
                <Badge className={`${section.color} font-normal`}>{section.placeholders.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {section.placeholders.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => handleCopy(p.name)}
                  className="w-full text-left rounded-md border border-transparent hover:border-primary/20 hover:bg-primary/5 px-3 py-2 transition-colors group"
                  data-testid={`template-var-${p.name}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <code className="text-xs font-mono text-primary truncate">«{p.name}»</code>
                    {copiedKey === p.name ? (
                      <Check className="w-3.5 h-3.5 text-green-600 shrink-0" />
                    ) : (
                      <Copy className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.desc}</div>
                  <div className="text-[11px] text-muted-foreground/80 mt-0.5 italic truncate">
                    es. → <span className="text-foreground/80 not-italic">{p.sample}</span>
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-muted/30">
        <CardContent className="py-4 text-xs text-muted-foreground space-y-2">
          <p>
            <strong>Nota tecnica:</strong> i placeholder sono case-sensitive ma sono stati registrati alias sia
            in PascalCase (es. <code>«Nome_Datore_Lavoro»</code>) sia in lowercase (es. <code>«nome_datore»</code>)
            per compatibilità con i template legacy. Se un placeholder restà vuoto dopo la generazione, verifica:
          </p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Che il nome sia esatto (anche maiuscole/minuscole).</li>
            <li>Che il dato sorgente sia compilato sulla scheda anagrafica (UL o dipendente).</li>
            <li>Che le virgolette siano <code>«»</code> (e non «&#x300A;&#x300B;» o <code>&lt;&lt; &gt;&gt;</code>).</li>
          </ol>
          <p>
            <strong>Hai bisogno di un nuovo placeholder?</strong> Contatta l'amministratore di sistema indicando
            il nome desiderato e quale dato deve contenere.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
